import { html, css, LitElement } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { hermesClient } from "../hermes-singleton.js";
import { runGatewayPoll } from "../gateway-poll-queue.ts";
import { canPollGateway } from "../poll-coordinator.ts";
import { icons } from "../icons.ts";
import { toSanitizedMarkdownHtml } from "../markdown.ts";
import {
  parsePfmNodeListResponse,
  type ParsedPfmNodeList,
} from "../pfm-node-list-parser.ts";
import {
  buildMindmapCacheKey,
  buildScopeKey,
  formatPathBreadcrumb,
  type DrillAncestor,
} from "../pfm-drill-keys.ts";
import { formatEadFeatureMapLabelFromServer } from "../pfm-version-label.ts";
import {
  loadPfmNodeReport,
  savePfmNodeReport,
  loadPendingPfmNodeReportRequest,
  savePendingPfmNodeReportRequest,
  clearPendingPfmNodeReportRequest,
  savePfmMindmap,
  clearPfmMindmap,
  type SavedPfmNodeReport,
  type PendingPfmNodeReportRequest,
} from "../../local-storage.ts";
import {
  clearPfmMindmapRemote,
  persistPfmMindmapRemote,
} from "../pfm-mindmap-remote-cache.ts";
import type { UiLocale } from "../i18n.ts";
import {
  buildNodeReportRequestPrompt,
  buildNodeReportTranslatePrompt,
  buildNodeReportWakePrompt,
  buildPfmNodePromptContext,
} from "../pfm-node-report-prompts.ts";
import {
  EAD_RUN_CHAT_UPDATED,
  notifyRunChatUpdated,
  type RunChatUpdatedDetail,
} from "../run-chat-events.ts";
import {
  nodeReportLanguageProfile,
  resolveNodeReportLanguage,
  type NodeReportLanguage,
} from "../node-report-language.ts";
import { t } from "../i18n.ts";
import {
  classifyNodeReportMarkdown,
  findMatchingNodeReportReply,
  isIncompleteExplorationNodeReport,
  isValidNodeReportMarkdown,
  NODE_REPORT_REPLY_RE,
  NODE_REPORT_REQUEST_RE,
  normalizeNodeReportMarkdown,
  stripNodeReportReplyMarker,
  type NodeReportTier,
} from "../pfm-node-report-validation.ts";

const NODE_REPORT_WAIT_MS = 8 * 60 * 1000;
const NODE_REPORT_POLL_MS = 8000;
const NODE_REPORT_FORMAT_REPAIR_MARKER = "[Node-Report-Format-Repair]";
const TRANSLATE_REPLY_RE = /\[Node-Report-Translate-Reply-To:\s*(REQ-TR-[A-Z0-9-]+)\s*\]/i;
const CHILD_NODE_REPLY_RE = /\[Child-Nodes-Reply-To:\s*(REQ-CHILD-[A-Z0-9-]+)\s*\]/i;
const CHILD_NODE_REQUEST_RE = /\[Child-Nodes-Request-Id:\s*(REQ-CHILD-[A-Z0-9-]+)\s*\]/i;

function generateNodeReportReqId(): string {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase().padEnd(4, "0");
  return `REQ-NODE-${ts}-${rand}`;
}

function generateChildNodeReqId(): string {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase().padEnd(4, "0");
  return `REQ-CHILD-${ts}-${rand}`;
}

function generateTranslateReqId(): string {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase().padEnd(4, "0");
  return `REQ-TR-${ts}-${rand}`;
}

function isMaterializedStubMarkdown(markdown: string): boolean {
  const md = String(markdown || "").trim();
  if (!md) return true;
  return md.includes("Materialized from run") && /execution `[^`]+`/i.test(md);
}

function buildChildNodePrompt(
  reqId: string,
  node: { nodeKey: string; title: string },
): string {
  return [
    `[Child-Nodes-Request-Id: ${reqId}]`,
    `Return the direct child PFM Nodes list only for ${node.title}.`,
    "",
    "Rules:",
    "- First output exactly one line: Child PFM Node Count: <number>",
    "- Output one node per line.",
    "- Number each node starting from 1.",
    "- Use this exact format:",
    "  <number>. <Node Key> - <Short Human Readable Title>",
    "- The Node Key should be short and stable, using lowercase words separated by hyphens.",
    "- The Short Human Readable Title should use normal spaces between words.",
    "- Do not use markdown tables.",
    "- Do not include headers, explanations, summaries, bullets, or extra commentary.",
    "- If there are no child nodes, output exactly: Child PFM Node Count: 0 and nothing else.",
    "",
    "Example:",
    "1. employee-portal - Employee Portal Admin",
    "2. team-portal - Team Portal Admin",
    "3. user-manager - User Manager",
    "",
    "IMPORTANT: Begin your reply with this exact line so the UI can match the answer:",
    `[Child-Nodes-Reply-To: ${reqId}]`,
  ].join("\n");
}

function buildChildNodeWakePrompt(reqId: string): string {
  return [
    `Please process the immediately previous request [Child-Nodes-Request-Id: ${reqId}].`,
    `Begin your answer with this exact line: [Child-Nodes-Reply-To: ${reqId}]`,
  ].join("\n");
}

@customElement("ead-pfm-node-report-panel")
export class EadPfmNodeReportPanel extends LitElement {
  static styles = css`
    :host {
      display: block;
    }

    .node-report-panel {
      border: 1px solid color-mix(in srgb, var(--border) 78%, transparent);
      border-radius: var(--radius-md, 10px);
      background: color-mix(in srgb, var(--card, #161920) 86%, transparent);
      overflow: hidden;
    }

    .node-report-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 12px;
      padding: 12px 14px;
      border-bottom: 1px solid color-mix(in srgb, var(--border) 72%, transparent);
    }

    .node-report-title {
      font-size: 13px;
      font-weight: 700;
      color: var(--text-strong);
    }

    .node-report-meta {
      margin-top: 3px;
      font-size: 11px;
      color: var(--muted);
    }

    .node-report-body {
      padding: 14px;
      font-size: 13px;
      line-height: 1.55;
      color: var(--text);
      overflow-wrap: anywhere;
    }

    .node-report-body :is(h1, h2, h3, h4) {
      margin: 16px 0 8px;
      color: var(--text-strong);
      line-height: 1.28;
    }

    .node-report-body h1 {
      font-size: 16px;
    }

    .node-report-body h2 {
      font-size: 14px;
      padding-bottom: 5px;
      border-bottom: 1px solid color-mix(in srgb, var(--border) 60%, transparent);
    }

    .node-report-body h3 {
      font-size: 13px;
      margin-top: 18px;
      padding: 8px 10px;
      border-radius: 8px;
      background: color-mix(in srgb, var(--bg-elevated, #161920) 88%, transparent);
      border: 1px solid color-mix(in srgb, var(--border) 60%, transparent);
    }

    .node-report-body h4 {
      font-size: 12px;
      color: var(--muted);
    }

    .node-report-body :is(ul, ol) {
      padding-left: 18px;
      margin: 6px 0 10px;
    }

    .node-report-body ol {
      list-style-position: outside;
      padding-left: 22px;
    }

    .node-report-body ol > li {
      margin: 10px 0;
      padding-left: 2px;
    }

    .node-report-body ol > li > ul {
      margin: 6px 0 2px;
      padding-left: 18px;
    }

    .node-report-body li {
      margin: 4px 0;
    }

    .node-report-body p {
      margin: 7px 0 10px;
    }

    .btn {
      padding: 5px 10px;
      border-radius: var(--radius-md, 10px);
      border: 1px solid color-mix(in srgb, var(--border) 88%, transparent);
      background: color-mix(in srgb, var(--bg-elevated) 80%, transparent);
      color: var(--text-strong, #f4f4f5);
      font-size: 11px;
      font-weight: 500;
      cursor: pointer;
    }

    .btn:hover:not(:disabled) {
      background: var(--bg-hover);
    }

    .btn:disabled {
      cursor: not-allowed;
      opacity: 0.55;
    }

    .btn-sm {
      padding: 4px 8px;
      font-size: 11px;
    }

    .btn-ghost {
      display: inline-flex;
      align-items: center;
      gap: 4px;
    }

    .refresh-menu-trigger {
      color: color-mix(in srgb, var(--text-strong, #f4f4f5) 94%, #ffffff);
      border-color: color-mix(in srgb, var(--border) 78%, transparent);
      background: color-mix(in srgb, var(--bg-elevated) 84%, transparent);
    }

    .refresh-menu-trigger svg {
      width: 14px;
      height: 14px;
      stroke: currentColor;
      fill: none;
      stroke-width: 1.9;
      stroke-linecap: round;
      stroke-linejoin: round;
      opacity: 0.98;
    }
  `;

  @property({ type: String }) executionId = "";
  @property({ type: String }) runStatus = "";
  @property({ type: String }) pfmSnapshotPhase = "";
  @property({ type: Number }) pfmGenerationVersion = 0;
  @property({ type: Number }) pfmRevision = 0;
  @property({ type: Boolean }) pfmFinalized = false;
  /**
   * When set, read committed node-report artifacts from this execution (template baseline).
   * Saves and chat generation still use {@link executionId}.
   */
  @property({ type: String }) pfmArtifactReadExecutionId = "";
  @property({ type: String }) sessionKey = "";
  @property({ type: String }) templateId = "";
  @property({ type: String }) runName = "";
  @property({ type: String }) targetUrl = "";
  @property({ type: String }) nodeKey = "";
  @property({ type: String }) title = "";
  @property({ attribute: false }) parents: DrillAncestor[] = [];
  /** Use detailed Refresh prompt (expanded QA bar). Default: concise. */
  @property({ type: Boolean }) refreshPromptDetailed = false;
  @property({ type: Boolean }) autoGenerate = false;
  /** When true, selecting a node auto-authors a full report (if not already full) and saves to DB. */
  @property({ type: Boolean }) autoGenerateOnDemand = true;
  @property({ type: String }) locale = "en";
  @property({ type: String }) aiAnswerLanguage = "";

  @state() private _report: SavedPfmNodeReport | null = null;
  @state() private _loading = false;
  @state() private _error = "";
  @state() private _statusNote = "";
  @state() private _pendingReqId = "";
  @state() private _pendingDeadline = 0;
  @state() private _pendingTick = 0;
  @state() private _childPendingReqId = "";
  @state() private _committedTreeVersion = 0;
  @state() private _committedTreeGeneratedAt = 0;
  @state() private _committedArtifactCreatedAt = 0;
  @state() private _committedReportSource: "agent-snapshot" | "chat-generated" | "none" = "none";
  @state() private _committedReportStale = false;
  @state() private _availability: "ready" | "baseline" | "pending_generation" | "none" = "none";
  @state() private _reportTier: NodeReportTier | "none" = "none";
  @state() private _reportRevision = 0;
  @state() private _agentGeneratingOnDemand = false;
  @state() private _sourceExecutionId = "";
  @state() private _refreshMenuOpen = false;
  @state() private _displayTranslation: string | null = null;
  @state() private _displayTranslationLang: NodeReportLanguage | null = null;
  @state() private _translating = false;

  private _watcher: ReturnType<typeof setInterval> | null = null;
  private _countdown: ReturnType<typeof setInterval> | null = null;
  private _childWatcher: ReturnType<typeof setInterval> | null = null;
  private _seq = 0;
  private _currentScopeKey = "";

  private readonly _onRunChatUpdated = (ev: Event): void => {
    const detail = (ev as CustomEvent<RunChatUpdatedDetail>).detail;
    const sessionKey = String(this.sessionKey || "").trim();
    if (!detail?.sessionKey || detail.sessionKey !== sessionKey) return;
    void this._tryIngestLatestChatReply();
  };

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener(EAD_RUN_CHAT_UPDATED, this._onRunChatUpdated);
    this._refreshForCurrentNode();
  }

  disconnectedCallback(): void {
    window.removeEventListener(EAD_RUN_CHAT_UPDATED, this._onRunChatUpdated);
    super.disconnectedCallback();
    this._stopWatcher();
  }

  protected willUpdate(changed: Map<string, unknown>): void {
    if (
      changed.has("executionId") ||
      changed.has("pfmArtifactReadExecutionId") ||
      changed.has("nodeKey") ||
      changed.has("parents") ||
      changed.has("sessionKey") ||
      changed.has("autoGenerate") ||
      changed.has("autoGenerateOnDemand")
    ) {
      this._refreshForCurrentNode();
    }
    if (changed.has("locale") || changed.has("aiAnswerLanguage")) {
      if (this._currentReportLanguage() === "en") {
        this._clearDisplayTranslation();
      }
    }
  }

  private _resolvedSnapshotPhase(): string {
    const explicit = String(this.pfmSnapshotPhase || "").trim();
    if (explicit) return explicit;
    const readBaseline = (this.pfmArtifactReadExecutionId || "").trim();
    const ownId = (this.executionId || "").trim();
    if (readBaseline && readBaseline !== ownId) return "baseline_preview";
    const st = String(this.runStatus || "").trim().toLowerCase();
    if (st === "completed" || st === "abandoned" || st === "failed" || st === "cancelled" || st === "error") return "final";
    if (st === "running" || st === "pending") return "evolving";
    return "final";
  }

  private _pfmVersionLabel(): string {
    return formatEadFeatureMapLabelFromServer({
      pfmGenerationVersion: this.pfmGenerationVersion,
      pfmRevision: this.pfmRevision,
      pfmFinalized: this.pfmFinalized,
      pfmSnapshotPhase: this._resolvedSnapshotPhase(),
    });
  }

  private _scopeKey(): string {
    return buildScopeKey(this.parents || [], this.nodeKey || "");
  }

  /** Stable key for in-flight loads (scope + artifact read execution + run id). */
  private _nodeLoadKey(): string {
    const scope = this._scopeKey();
    if (!scope || !this.executionId) return "";
    return `${scope}|${this._readArtifactExecutionId()}|${this.executionId}`;
  }

  private _childCacheKey(): string {
    const ancestorsAndSelf = [
      ...(this.parents || []),
      { nodeKey: this.nodeKey, title: this.title },
    ];
    return buildMindmapCacheKey(this.sessionKey || "", ancestorsAndSelf);
  }

  private _readArtifactExecutionId(): string {
    return (this.pfmArtifactReadExecutionId || "").trim() || this.executionId;
  }

  private _inheritsBaselineArtifacts(): boolean {
    const read = this._readArtifactExecutionId();
    const own = (this.executionId || "").trim();
    return Boolean(read && own && read !== own);
  }

  private _refreshForCurrentNode(): void {
    const scope = this._scopeKey();
    const loadKey = this._nodeLoadKey();
    if (!scope || !this.executionId) {
      this._stopWatcher();
      this._report = null;
      this._error = "";
      this._statusNote = "";
      this._pendingReqId = "";
      this._pendingDeadline = 0;
      this._pendingTick = 0;
      this._childPendingReqId = "";
      this._loading = false;
      this._currentScopeKey = "";
      this._stopChildWatcher();
      this._clearDisplayTranslation();
      this._refreshMenuOpen = false;
      return;
    }
    if (loadKey === this._currentScopeKey) return;
    this._currentScopeKey = loadKey;

    this._stopWatcher();
    this._error = "";
    this._statusNote = "";
    this._pendingReqId = "";
    this._pendingDeadline = 0;
    this._pendingTick = 0;
    this._childPendingReqId = "";
    this._loading = false;
    this._stopChildWatcher();
    this._clearDisplayTranslation();
    this._refreshMenuOpen = false;

    const cached = loadPfmNodeReport(this.executionId, scope);
    this._report = cached;
    this._committedTreeVersion = 0;
    this._committedTreeGeneratedAt = 0;
    this._committedArtifactCreatedAt = 0;
    this._committedReportSource = cached ? "chat-generated" : "none";
    this._committedReportStale = false;
    this._availability = cached ? "ready" : "none";
    this._reportTier =
      cached && !isMaterializedStubMarkdown(cached.content) ? "full" : "none";
    this._reportRevision = 0;
    this._agentGeneratingOnDemand = false;
    this._sourceExecutionId = "";
    this._loading = !cached;
    void this._hydrateCommittedThenMaybeGenerate(scope, cached);
  }

  private async _hydrateCommittedThenMaybeGenerate(
    scope: string,
    cached: SavedPfmNodeReport | null,
  ): Promise<void> {
    try {
      await this._loadCommittedNodeReport(scope, false);
    } finally {
      if (this._nodeLoadKey() === this._currentScopeKey) {
        this._loading = false;
      }
    }
    // Re-evaluate after committed DB artifact load so we don't ask the agent
    // to regenerate when FMR-backed node reports already exist.
    if (this.sessionKey && this._nodeLoadKey() === this._currentScopeKey) {
      await this._resumePendingIfAny();
    }
  }

  private _shouldAutoGenerateOnDemand(): boolean {
    if (!this.autoGenerateOnDemand) return false;
    if (!this.sessionKey || !this.executionId) return false;
    if (this._pendingReqId || this._agentGeneratingOnDemand) return false;
    if (this._inheritsBaselineArtifacts()) return false;
    if (this._availability === "baseline") return false;
    if (this._availability === "ready" && this._reportTier === "full") return false;
    if (this._reportTier === "full") return false;
    if (
      this._report?.content
      && isValidNodeReportMarkdown(this._report.content)
      && !isMaterializedStubMarkdown(this._report.content)
    ) {
      return false;
    }
    if (
      this._report?.content
      && isValidNodeReportMarkdown(this._report.content)
      && !isMaterializedStubMarkdown(this._report.content)
      && this._availability === "ready"
    ) {
      return false;
    }
    if (this._reportTier === "summary" || this._reportTier === "incomplete") return true;
    if (this._availability === "pending_generation" || this._availability === "none") {
      return true;
    }
    if (this._report && isMaterializedStubMarkdown(this._report.content)) {
      return true;
    }
    return false;
  }

  private async _maybeAutoGenerateOnDemand(scope: string): Promise<void> {
    if (this._nodeLoadKey() !== this._currentScopeKey) return;
    if (!this._shouldAutoGenerateOnDemand()) return;
    await this._generate(false);
  }

  private async _initialResumeOrAutoGenerate(_cached: SavedPfmNodeReport | null): Promise<void> {
    const scope = this._scopeKey();
    if (!scope || !this.executionId) return;

    if (this.sessionKey) {
      const resumed = await this._resumePendingIfAny();
      if (resumed) return;
    }

    if (this.autoGenerateOnDemand) {
      await this._maybeAutoGenerateOnDemand(scope);
      return;
    }

    if (!this.autoGenerate) {
      if (this.sessionKey) {
        clearPendingPfmNodeReportRequest(this.executionId, scope);
      }
      return;
    }

    if (!this.sessionKey) return;
    if (!_cached && this.autoGenerate) {
      void this._generate(false);
    }
  }

  private async _loadCommittedNodeReport(
    scope: string,
    allowBaseline = false,
  ): Promise<void> {
    const readExec = String(this.executionId || "").trim();
    const nodeKey = String(this.nodeKey || "").trim();
    if (!readExec || !nodeKey) return;
    try {
      const resp = await hermesClient.request<{
        availability?: "ready" | "baseline" | "pending_generation" | "none";
        reportTier?: "full" | "summary" | "none";
        reportRevision?: number;
        sourceExecutionId?: string;
        resolvedNodeKey?: string;
        artifact?: { content?: string; createdAt?: number; title?: string; nodeKey?: string } | null;
        pfmTreeVersion?: number;
        pfmTreeGeneratedAt?: number;
        isStale?: boolean;
      }>(
        allowBaseline ? "pfmNodeReports.getWithBaseline" : "pfmNodeReports.get",
        {
          executionId: readExec,
          nodeKey,
          selectedNodeOnly: allowBaseline ? undefined : true,
        },
      );
      // If selection changed while loading, abandon.
      if (this._nodeLoadKey() !== this._currentScopeKey) return;
      this._availability = resp?.availability || "none";
      this._reportTier = resp?.reportTier || "none";
      this._reportRevision = Number(resp?.reportRevision || 0);
      this._sourceExecutionId = String(resp?.sourceExecutionId || readExec).trim();
      this._committedTreeVersion = Number(resp?.pfmTreeVersion || 0);
      this._committedTreeGeneratedAt = Number(resp?.pfmTreeGeneratedAt || 0);
      const artifact = resp?.artifact;
      if (this._availability === "pending_generation") {
        this._report = null;
        this._clearDisplayTranslation();
        this._committedReportSource = "none";
        this._committedReportStale = false;
        this._committedArtifactCreatedAt = 0;
        return;
      }
      if (artifact && artifact.content) {
        const content = String(artifact.content || "");
        const createdAt = Number(artifact.createdAt || 0);
        this._committedArtifactCreatedAt = createdAt;
        this._committedReportStale = Boolean(resp?.isStale);
        // Prefer the agent-authored snapshot as the source of truth.
        this._report = {
          executionId: this.executionId,
          sessionKey: this.sessionKey || "",
          nodeKey: String(this.nodeKey || scope).trim(),
          title: String(artifact.title || this.title || nodeKey),
          content,
          generatedAt: createdAt || Date.now(),
        };
        this._clearDisplayTranslation();
        savePfmNodeReport(this._report);
        this._committedReportSource = "agent-snapshot";
        return;
      }
      this._committedReportSource = this._report ? "chat-generated" : "none";
      this._committedReportStale = false;
    } catch (error) {
      console.warn("[node-report-panel] failed to load committed node report", error);
    }
  }

  private async _resumePendingIfAny(): Promise<boolean> {
    const scope = this._scopeKey();
    const pending = loadPendingPfmNodeReportRequest(this.executionId, scope);
    if (!pending) return false;
    if (Date.now() > pending.sentAt + NODE_REPORT_WAIT_MS) {
      clearPendingPfmNodeReportRequest(this.executionId, scope);
      return false;
    }
    if (await this._tryIngestLatestChatReply(pending)) return true;
    const messages = await this._readLatestChatMessages(pending.sessionKey).catch(() => []);
    const requestExists = messages.some((msg) => {
      if ((msg.role || "") !== "user") return false;
      const marker = String(msg.content || "").match(NODE_REPORT_REQUEST_RE);
      return marker && marker[1].toUpperCase() === pending.reqId.toUpperCase();
    });
    if (!requestExists) {
      // Request may have scrolled out of the history window; keep watching for the reply.
      this._startWatcher(pending);
      return true;
    }
    this._startWatcher(pending);
    return true;
  }

  /** Pull the latest tagged node-report reply from chat into the panel + DB. */
  private async _tryIngestLatestChatReply(
    pending?: PendingPfmNodeReportRequest | null,
  ): Promise<boolean> {
    const sessionKey = String(this.sessionKey || "").trim();
    const scope = this._scopeKey();
    if (!sessionKey || !scope || !this.executionId) return false;

    const active =
      pending ?? loadPendingPfmNodeReportRequest(this.executionId, scope);
    const messages = await this._readLatestChatMessages(sessionKey).catch(() => []);
    const match = findMatchingNodeReportReply(messages, {
      pendingReqId: active?.reqId,
      nodeKey: String(this.nodeKey || "").trim(),
    });
    if (!match) return false;

    const synthetic: PendingPfmNodeReportRequest = active ?? {
      reqId: match.reqId,
      executionId: this.executionId,
      sessionKey,
      nodeKey: String(this.nodeKey || scope).trim(),
      title: this.title,
      sentAt: Date.now(),
      prompt: "",
    };
    return this._acceptReply(synthetic, match.content, false);
  }

  private async _reloadFromDb(): Promise<void> {
    const scope = this._scopeKey();
    if (!scope || !this.executionId) return;
    this._loading = true;
    this._error = "";
    this._statusNote = "";
    try {
      await this._loadCommittedNodeReport(scope, false);
    } finally {
      if (this._nodeLoadKey() === this._currentScopeKey) {
        this._loading = false;
      }
    }
  }

  private async _generate(explicitRegenerate: boolean): Promise<void> {
    const wantsAgentFullReport =
      this.autoGenerate
      || this._shouldAutoGenerateOnDemand()
      || explicitRegenerate;
    if (!wantsAgentFullReport) {
      await this._reloadFromDb();
      return;
    }
    const sessionKey = String(this.sessionKey || "").trim();
    const scope = this._scopeKey();
    if (!sessionKey) {
      this._error = "No run chat session is available for this node report.";
      return;
    }
    if (!scope || !this.executionId) return;
    if (this._pendingReqId) return;
    this._clearDisplayTranslation();
    this._refreshMenuOpen = false;

    if (explicitRegenerate) {
      await this._loadCommittedNodeReport(scope, true);
    }

    const seq = ++this._seq;
    this._agentGeneratingOnDemand = true;
    const reqId = generateNodeReportReqId();
    const baselineMarkdown =
      explicitRegenerate && this._report?.content
        ? this._report.content
        : "";
    const baselineIsFull = baselineMarkdown
      ? isValidNodeReportMarkdown(baselineMarkdown)
      : true;
    const baselineSourceLabel =
      this._availability === "baseline"
        ? `baseline from run ${this._sourceExecutionId || "prior"}`
        : this._availability === "ready"
          ? "current run saved report"
          : "latest available saved report";
    const promptCtx = buildPfmNodePromptContext({
      executionId: this.executionId,
      templateId: this.templateId,
      runName: this.runName,
      targetUrl: this.targetUrl,
      nodeKey: String(this.nodeKey || "").trim(),
      nodeTitle: String(this.title || this.nodeKey || "").trim(),
      parents: this.parents || [],
    });
    const prompt = buildNodeReportRequestPrompt({
      reqId,
      ctx: promptCtx,
      refresh: explicitRegenerate,
      baselineMarkdown,
      baselineSourceLabel,
      detailed: this.refreshPromptDetailed,
      locale: this.locale as UiLocale,
      aiAnswerLanguage: this.aiAnswerLanguage,
      baselineGeneratedAtMs: this._report?.generatedAt || 0,
      baselineIsFull,
    });

    const pending: PendingPfmNodeReportRequest = {
      reqId,
      executionId: this.executionId,
      sessionKey,
      nodeKey: String(this.nodeKey || scope).trim(),
      title: this.title,
      sentAt: Date.now(),
      prompt,
      baselineMarkdown: baselineMarkdown || undefined,
    };
    savePendingPfmNodeReportRequest(pending);
    this._pendingReqId = reqId;
    this._pendingDeadline = pending.sentAt + NODE_REPORT_WAIT_MS;
    this._pendingTick = Date.now();
    this._loading = false;
    this._error = "";

    try {
      notifyRunChatUpdated({ sessionKey, userMessage: prompt });
      const response = await hermesClient.request<{ response?: string; error?: string }>(
        "chat.send",
        explicitRegenerate
          ? {
              sessionKey,
              message: prompt,
              deliver: true,
            }
          : {
              sessionKey,
              message: prompt,
              deliver: false,
            },
      );
      notifyRunChatUpdated({ sessionKey });
      void this._tryIngestLatestChatReply(pending);
      if (!explicitRegenerate) {
        if (!response) {
          clearPendingPfmNodeReportRequest(this.executionId, scope);
          this._pendingReqId = "";
          this._pendingDeadline = 0;
          this._pendingTick = 0;
          this._agentGeneratingOnDemand = false;
          this._error = "Unable to save the node report request. Please try again.";
          return;
        }
        const wakePrompt = buildNodeReportWakePrompt(reqId, {
          locale: this.locale as UiLocale,
          aiAnswerLanguage: this.aiAnswerLanguage,
        });
        notifyRunChatUpdated({ sessionKey, userMessage: wakePrompt });
        const wakeResponse = await hermesClient.request<{ response?: string; error?: string }>(
          "chat.send",
          {
            sessionKey,
            message: wakePrompt,
            deliver: true,
          },
        );
        notifyRunChatUpdated({ sessionKey });
        if (seq !== this._seq) return;
        const wakeContent = String(wakeResponse?.response || "").trim();
        if (!wakeResponse?.error && (await this._acceptReply(pending, wakeContent, true))) {
          return;
        }
        if (await this._tryIngestLatestChatReply(pending)) return;
        if (wakeResponse?.error) {
          this._error = wakeResponse.error;
        }
        this._startWatcher(pending);
        return;
      }
      if (seq !== this._seq) return;
      const content = String(response?.response || "").trim();
      if (!response?.error && (await this._acceptReply(pending, content, true))) {
        return;
      }
      if (await this._tryIngestLatestChatReply(pending)) return;
      if (response?.error) {
        this._error = response.error;
      }
      this._startWatcher(pending);
    } catch (error) {
      if (seq !== this._seq) return;
      console.warn("[node-report-panel] chat.send failed; will watch chat history.", error);
      this._startWatcher(pending);
    } finally {
      if (seq === this._seq && !this._pendingReqId) {
        this._agentGeneratingOnDemand = false;
      }
    }
  }

  private async _acceptReply(
    pending: PendingPfmNodeReportRequest,
    rawContent: string,
    allowUnmarked: boolean,
  ): Promise<boolean> {
    const scope = this._scopeKey();
    const raw = String(rawContent || "").trim();
    if (!raw) return false;
    const marker = raw.match(NODE_REPORT_REPLY_RE);
    if (
      marker
      && pending.reqId
      && marker[1].toUpperCase() !== pending.reqId.toUpperCase()
    ) {
      return false;
    }
    if (!marker && !allowUnmarked) return false;

    let reportText = normalizeNodeReportMarkdown(raw);
    if (/^no_new_findings\s*:/i.test(reportText)) {
      const fromPending = String(pending.baselineMarkdown || "").trim();
      const fromSaved = String(this._report?.content || "").trim();
      const fromPrompt = pending.prompt.match(
        /----- BEGIN BASELINE(?: REPORT)? -----\n([\s\S]*?)\n----- END BASELINE/,
      );
      const baselineBody = fromPending || fromSaved || fromPrompt?.[1]?.trim() || "";
      reportText = normalizeNodeReportMarkdown(baselineBody);
    }
    const tier = classifyNodeReportMarkdown(reportText);
    if (!isValidNodeReportMarkdown(reportText) && tier !== "incomplete") {
      const alreadyRetried = pending.prompt.includes(NODE_REPORT_FORMAT_REPAIR_MARKER);
      if (!alreadyRetried) {
        const repairPrompt = this._buildLongFormatRepairPrompt(pending.reqId, raw);
        const repairedPending: PendingPfmNodeReportRequest = {
          ...pending,
          prompt: `${pending.prompt}\n\n${NODE_REPORT_FORMAT_REPAIR_MARKER}`,
        };
        savePendingPfmNodeReportRequest(repairedPending);
        try {
          notifyRunChatUpdated({ sessionKey: pending.sessionKey, userMessage: repairPrompt });
          const repairResp = await hermesClient.request<{ response?: string; error?: string }>("chat.send", {
            sessionKey: pending.sessionKey,
            message: repairPrompt,
            deliver: true,
          });
          notifyRunChatUpdated({ sessionKey: pending.sessionKey });
          const repaired = String(repairResp?.response || "").trim();
          if (!repairResp?.error && repaired) {
            if (await this._acceptReply(repairedPending, repaired, true)) return true;
          }
        } catch {
          // Fall through to default invalid-format error below.
        }
      }
      if (await this._tryRepopulateFromCommittedFull(pending, scope)) {
        return true;
      }
      this._error =
        "Agent replied in chat, but the report is not a full node report with Features, Test Cases, and Procedure Steps. Click Refresh to try again.";
      return false;
    }

    const report: SavedPfmNodeReport = {
      executionId: pending.executionId,
      sessionKey: pending.sessionKey,
      nodeKey: String(this.nodeKey || scope || pending.nodeKey).trim(),
      title: pending.title,
      content: reportText,
      generatedAt: Date.now(),
    };
    savePfmNodeReport(report);

    const saved = await hermesClient.request<{
      artifactKey?: string;
      nodeKey?: string;
      reportTier?: NodeReportTier | "none";
      reportRevision?: number;
      pfmNodeReport?: string;
      refreshOutcome?: "updated" | "no_new_findings";
      hasMeaningfulChange?: boolean;
      message?: string;
    }>(
      "pfmNodeReports.save",
      {
        executionId: report.executionId,
        nodeKey: String(this.nodeKey || "").trim() || report.nodeKey,
        title: report.title,
        content: report.content,
      },
    );
    if (!saved) {
      this._error =
        hermesClient.getLastRequestError() ||
        "Could not save the node report to the database. The report is kept locally only.";
      this._statusNote = "";
      this._report = report;
      this._loading = false;
      this._pendingReqId = "";
      this._pendingDeadline = 0;
      this._pendingTick = 0;
      this._stopWatcher();
      clearPendingPfmNodeReportRequest(pending.executionId, scope || pending.nodeKey);
      return true;
    }

    clearPendingPfmNodeReportRequest(pending.executionId, scope || pending.nodeKey);
    const savedBody = String(saved.pfmNodeReport || report.content || "").trim();
    this._report = {
      ...report,
      content: savedBody || report.content,
    };
    this._clearDisplayTranslation();
    savePfmNodeReport(this._report);
    this._availability = "ready";
    this._reportTier =
      saved.reportTier === "full"
      || saved.reportTier === "summary"
      || saved.reportTier === "incomplete"
        ? saved.reportTier
        : tier === "full" || tier === "incomplete"
          ? tier
          : "summary";
    this._reportRevision = Number(saved.reportRevision || this._reportRevision || 0);
    this._committedReportSource = "agent-snapshot";
    this._error = "";
    this._statusNote = saved.refreshOutcome === "no_new_findings"
      ? String(
          saved.message ||
            "No newer activity/evidence was found for this node; existing report kept.",
        )
      : tier === "incomplete"
        ? this._t("nodeReport.incompleteExplorationSaved")
        : tier === "summary"
          ? "Saved a brief summary from chat. Click Refresh again if you need full Features and Test Cases."
          : "";
    this._loading = false;
    this._pendingReqId = "";
    this._pendingDeadline = 0;
    this._pendingTick = 0;
    this._stopWatcher();
    this._agentGeneratingOnDemand = false;

    void this._loadCommittedNodeReport(scope, false);
    this.dispatchEvent(
      new CustomEvent("pfm-node-report-ready", {
        detail: {
          scopeKey: scope,
          nodeKey: this.nodeKey,
          childCacheKey: this._childCacheKey(),
          hasChildren: false,
          childCount: 0,
        },
        bubbles: true,
        composed: true,
      }),
    );

    void this._requestChildNodes(pending);

    return true;
  }

  private async _tryRepopulateFromCommittedFull(
    pending: PendingPfmNodeReportRequest,
    scope: string,
  ): Promise<boolean> {
    try {
      await this._loadCommittedNodeReport(scope, true);
      const recovered = normalizeNodeReportMarkdown(String(this._report?.content || "").trim());
      if (!recovered || !isValidNodeReportMarkdown(recovered)) return false;
      const report: SavedPfmNodeReport = {
        executionId: pending.executionId,
        sessionKey: pending.sessionKey,
        nodeKey: String(this.nodeKey || scope || pending.nodeKey).trim(),
        title: pending.title,
        content: recovered,
        generatedAt: Date.now(),
      };
      const saved = await hermesClient.request<{
        reportTier?: "full" | "summary" | "none";
        reportRevision?: number;
        pfmNodeReport?: string;
      }>("pfmNodeReports.save", {
        executionId: report.executionId,
        nodeKey: String(this.nodeKey || "").trim() || report.nodeKey,
        title: report.title,
        content: report.content,
      });
      const savedBody = String(saved?.pfmNodeReport || report.content || "").trim();
      this._report = { ...report, content: savedBody || report.content };
      savePfmNodeReport(this._report);
      this._clearDisplayTranslation();
      this._availability = "ready";
      this._reportTier = "full";
      this._reportRevision = Number(saved?.reportRevision || this._reportRevision || 0);
      this._committedReportSource = "agent-snapshot";
      this._error = "";
      this._statusNote = "Regenerated reply was not qualified. Re-populated from latest committed FMR node report.";
      this._loading = false;
      this._pendingReqId = "";
      this._pendingDeadline = 0;
      this._pendingTick = 0;
      this._stopWatcher();
      this._agentGeneratingOnDemand = false;
      clearPendingPfmNodeReportRequest(pending.executionId, scope || pending.nodeKey);
      return true;
    } catch {
      return false;
    }
  }

  private _buildLongFormatRepairPrompt(reqId: string, previousReply: string): string {
    const baseline = String(this._report?.content || "").trim();
    const baselineSnippet = baseline.length > 6000 ? `${baseline.slice(0, 6000)}\n...(truncated)` : baseline;
    const previousSnippet = String(previousReply || "").trim().slice(0, 4000);
    return [
      `[Node-Report-Request-Id: ${reqId}]`,
      "[Node-Report-Format-Repair]",
      "",
      `Rewrite your previous answer into a FULL node report only. Reply line 1 must be exactly: [Node-Report-Reply-To: ${reqId}]`,
      "Mandatory structure: Main Features -> Feature -> Test Case -> Procedure Steps (numbered) with Expected Result per step.",
      "Do not return no_new_findings. Do not summarize. Do not output activity logs.",
      "If evidence is missing, keep structure and write 'Not observed yet' in fields instead of omitting sections.",
      "",
      "Previous invalid answer (for correction):",
      "----- BEGIN PREVIOUS ANSWER -----",
      previousSnippet,
      "----- END PREVIOUS ANSWER -----",
      "",
      "Current baseline report:",
      "----- BEGIN BASELINE REPORT -----",
      baselineSnippet || "NONE",
      "----- END BASELINE REPORT -----",
    ].join("\n");
  }

  private _persistChildren(childListText: string): { hasChildren: boolean; childCount: number } {
    const cacheKey = this._childCacheKey();
    if (!cacheKey) return { hasChildren: false, childCount: 0 };
    const text = String(childListText || "").trim();
    if (!text) {
      clearPfmMindmap(cacheKey);
      void clearPfmMindmapRemote(this.sessionKey, cacheKey);
      return { hasChildren: false, childCount: 0 };
    }
    const countLine = text.match(/Child\s+PFM\s+Node\s+Count\s*:\s*(\d+)/i);
    const cleanedForParse = text
      .replace(/^\s*Child\s+PFM\s+Node\s+Count\s*:\s*\d+\s*$/im, "")
      .trim();
    let parsed: ParsedPfmNodeList;
    try {
      parsed = parsePfmNodeListResponse(cleanedForParse);
    } catch (error) {
      console.warn("[node-report-panel] Failed to parse child PFM node list:", error);
      clearPfmMindmap(cacheKey);
      void clearPfmMindmapRemote(this.sessionKey, cacheKey);
      return { hasChildren: false, childCount: 0 };
    }
    if (!parsed.nodes.length) {
      clearPfmMindmap(cacheKey);
      void clearPfmMindmapRemote(this.sessionKey, cacheKey);
      return { hasChildren: false, childCount: 0 };
    }
    const savedMindmap = {
      sessionKey: cacheKey,
      title: parsed.title || `${this.title} - children`,
      description: parsed.description || "",
      nodes: parsed.nodes,
      savedAt: Date.now(),
    };
    savePfmMindmap(savedMindmap);
    void persistPfmMindmapRemote(this.sessionKey, cacheKey, savedMindmap);
    const parsedCount = parsed.nodes.length;
    const declaredCount = countLine ? Number(countLine[1]) : 0;
    return { hasChildren: true, childCount: declaredCount > 0 ? declaredCount : parsedCount };
  }

  private _dispatchNodeReady(hasChildren: boolean, childCount = 0): void {
    // Child-list parsed for drill-down cache only. Must NOT trigger map delivery rebuild or requestSnapshot.
    this.dispatchEvent(new CustomEvent("pfm-node-report-ready", {
      detail: {
        scopeKey: this._scopeKey(),
        nodeKey: this.nodeKey,
        childCacheKey: this._childCacheKey(),
        hasChildren,
        childCount,
      },
      bubbles: true,
      composed: true,
    }));
  }

  private _startChildWatcher(
    pending: PendingPfmNodeReportRequest,
    childReqId: string,
    childRequestIndexHint: number,
  ): void {
    this._stopChildWatcher();
    this._childPendingReqId = childReqId;
    const childStartedAt = Date.now();
    this._childWatcher = setInterval(() => {
      void this._childWatcherTick(pending, childReqId, childStartedAt, childRequestIndexHint);
    }, NODE_REPORT_POLL_MS);
    void this._childWatcherTick(pending, childReqId, childStartedAt, childRequestIndexHint);
  }

  private _stopChildWatcher(): void {
    if (this._childWatcher != null) {
      clearInterval(this._childWatcher);
      this._childWatcher = null;
    }
    this._childPendingReqId = "";
  }

  private _acceptChildReply(rawContent: string, childReqId: string): boolean {
    const raw = String(rawContent || "").trim();
    if (!raw) return false;
    const marker = raw.match(CHILD_NODE_REPLY_RE);
    if (marker && marker[1].toUpperCase() !== childReqId.toUpperCase()) return false;
    const text = marker ? raw.replace(CHILD_NODE_REPLY_RE, "").trim() : raw;
    const parsed = this._persistChildren(text);
    this._dispatchNodeReady(parsed.hasChildren, parsed.childCount);
    this._stopChildWatcher();
    return true;
  }

  private async _childWatcherTick(
    pending: PendingPfmNodeReportRequest,
    childReqId: string,
    childStartedAt: number,
    childRequestIndexHint: number,
  ): Promise<void> {
    if (!canPollGateway()) return;
    if (Date.now() - childStartedAt > NODE_REPORT_WAIT_MS) {
      this._persistChildren("");
      this._dispatchNodeReady(false, 0);
      this._stopChildWatcher();
      return;
    }
    const messages = await this._readLatestChatMessages(pending.sessionKey).catch(() => []);
    let requestIdx = -1;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if ((msg.role || "") !== "user") continue;
      const marker = String(msg.content || "").match(CHILD_NODE_REQUEST_RE);
      if (marker && marker[1].toUpperCase() === childReqId.toUpperCase()) {
        requestIdx = i;
        break;
      }
    }
    if (requestIdx < 0) requestIdx = childRequestIndexHint;
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if ((msg.role || "") !== "assistant") continue;
      const content = String(msg.content || "");
      const marker = content.match(CHILD_NODE_REPLY_RE);
      if (marker && marker[1].toUpperCase() === childReqId.toUpperCase()) {
        this._acceptChildReply(content, childReqId);
        return;
      }
    }
    if (requestIdx >= 0) {
      for (let i = messages.length - 1; i > requestIdx; i -= 1) {
        const msg = messages[i];
        if ((msg.role || "") !== "assistant") continue;
        const text = String(msg.content || "").trim();
        const parsedList = parsePfmNodeListResponse(text);
        if (!parsedList.nodes.length) continue;
        const persisted = this._persistChildren(text);
        this._dispatchNodeReady(persisted.hasChildren, persisted.childCount);
        this._stopChildWatcher();
        return;
      }
    }
  }

  private async _requestChildNodes(pending: PendingPfmNodeReportRequest): Promise<void> {
    const childReqId = generateChildNodeReqId();
    const node = { nodeKey: this.nodeKey, title: this.title };
    const childPrompt = buildChildNodePrompt(childReqId, node);
    this._persistChildren("");
    this._dispatchNodeReady(false, 0);
    this._childPendingReqId = childReqId;
    try {
      const persisted = await hermesClient.request("chat.send", {
        sessionKey: pending.sessionKey,
        message: childPrompt,
        deliver: false,
      });
      if (!persisted) {
        this._dispatchNodeReady(false, 0);
        this._stopChildWatcher();
        return;
      }
      const wakeResp = await hermesClient.request<{ response?: string; error?: string }>("chat.send", {
        sessionKey: pending.sessionKey,
        message: buildChildNodeWakePrompt(childReqId),
        deliver: true,
      });
      const content = String(wakeResp?.response || "").trim();
      if (!wakeResp?.error && this._acceptChildReply(content, childReqId)) {
        return;
      }
      const messages = await this._readLatestChatMessages(pending.sessionKey).catch(() => []);
      let requestIndexHint = -1;
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        const msg = messages[i];
        if ((msg.role || "") !== "user") continue;
        const marker = String(msg.content || "").match(CHILD_NODE_REQUEST_RE);
        if (marker && marker[1].toUpperCase() === childReqId.toUpperCase()) {
          requestIndexHint = i;
          break;
        }
      }
      this._startChildWatcher(pending, childReqId, requestIndexHint);
    } catch {
      this._dispatchNodeReady(false, 0);
      this._stopChildWatcher();
    }
  }

  private _looksLikeNodeReport(content: string): boolean {
    return isValidNodeReportMarkdown(content);
  }

  private _startWatcher(pending: PendingPfmNodeReportRequest): void {
    this._stopWatcher();
    this._pendingReqId = pending.reqId;
    this._pendingDeadline = pending.sentAt + NODE_REPORT_WAIT_MS;
    this._pendingTick = Date.now();
    this._loading = true;

    this._countdown = setInterval(() => {
      this._pendingTick = Date.now();
    }, 1000);
    this._watcher = setInterval(() => {
      void this._watcherTick(pending);
    }, NODE_REPORT_POLL_MS);
    void this._watcherTick(pending);
  }

  private _stopWatcher(): void {
    if (this._watcher != null) {
      clearInterval(this._watcher);
      this._watcher = null;
    }
    if (this._countdown != null) {
      clearInterval(this._countdown);
      this._countdown = null;
    }
    this._stopChildWatcher();
  }

  private async _watcherTick(pending: PendingPfmNodeReportRequest): Promise<void> {
    if (!canPollGateway()) return;
    const scope = this._scopeKey();
    if (!scope || scope !== pending.nodeKey) return;
    if (Date.now() > pending.sentAt + NODE_REPORT_WAIT_MS) {
      clearPendingPfmNodeReportRequest(pending.executionId, pending.nodeKey);
      this._stopWatcher();
      this._pendingReqId = "";
      this._pendingDeadline = 0;
      this._pendingTick = 0;
      this._loading = false;
      this._agentGeneratingOnDemand = false;
      this._error = "PFM node report was not generated in time. The agent may still be busy or did not return the expected report format. Showing the previous saved report.";
      return;
    }

    if (await this._tryIngestLatestChatReply(pending)) return;

    const messages = await this._readLatestChatMessages(pending.sessionKey).catch(() => []);
    const match = findMatchingNodeReportReply(messages, {
      pendingReqId: pending.reqId,
      nodeKey: String(this.nodeKey || "").trim(),
    });
    if (match) {
      const adjusted = { ...pending, reqId: match.reqId };
      if (await this._acceptReply(adjusted, match.content, false)) return;
    }
  }

  private async _readLatestChatMessages(
    sessionKey: string,
  ): Promise<Array<{ role?: string; content?: string }>> {
    return runGatewayPoll(async () => {
      // Report replies can arrive after busy run-agent/tool chatter, so keep a wider tail.
      const pageSize = 300;
      const firstPage = await hermesClient.request<{
        messages?: Array<{ role?: string; content?: string }>;
        total?: number;
      }>("chat.history", { sessionKey, limit: 1, offset: 0 });
      const total = Number(firstPage?.total || firstPage?.messages?.length || 0);
      const offset = Math.max(0, total - pageSize);
      const result = await hermesClient.request<{
        messages?: Array<{ role?: string; content?: string }>;
      }>("chat.history", { sessionKey, limit: pageSize, offset });
      return result?.messages ?? [];
    });
  }

  private _cancelPending(): void {
    if (!this._pendingReqId) return;
    const scope = this._scopeKey();
    if (scope) clearPendingPfmNodeReportRequest(this.executionId, scope);
    this._stopWatcher();
    this._pendingReqId = "";
    this._pendingDeadline = 0;
    this._pendingTick = 0;
    this._loading = false;
    this._agentGeneratingOnDemand = false;
    this._error = "Stopped waiting for the node report. Showing the previous saved report.";
  }

  private _clearDisplayTranslation(): void {
    this._displayTranslation = null;
    this._displayTranslationLang = null;
  }

  private _currentReportLanguage(): NodeReportLanguage {
    return resolveNodeReportLanguage({
      aiAnswerLanguage: this.aiAnswerLanguage,
      locale: this.locale as UiLocale,
    });
  }

  private _stripTranslateReplyMarker(raw: string): string {
    return String(raw || "").replace(TRANSLATE_REPLY_RE, "").trim();
  }

  private _findLatestTranslateReply(
    messages: Array<{ role?: string; content?: string }>,
    reqId: string,
  ): string {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const msg = messages[i];
      if ((msg.role || "") !== "assistant") continue;
      const content = String(msg.content || "");
      const marker = content.match(TRANSLATE_REPLY_RE);
      if (marker && marker[1].toUpperCase() === reqId.toUpperCase()) {
        return this._stripTranslateReplyMarker(content);
      }
    }
    return "";
  }

  private async _translateForDisplay(): Promise<void> {
    const reportBody = String(this._report?.content || "").trim();
    const sessionKey = String(this.sessionKey || "").trim();
    const targetLang = this._currentReportLanguage();
    if (!reportBody || !sessionKey || targetLang === "en" || this._pendingReqId || this._translating) return;
    if (!isValidNodeReportMarkdown(reportBody)) {
      this._error = this._t("nodeReport.longFormatRequired");
      return;
    }

    this._refreshMenuOpen = false;
    this._translating = true;
    this._error = "";
    const reqId = generateTranslateReqId();
    const prompt = buildNodeReportTranslatePrompt({
      reqId,
      sourceMarkdown: reportBody,
      targetLang,
    });
    try {
      notifyRunChatUpdated({ sessionKey, userMessage: prompt });
      const response = await hermesClient.request<{ response?: string; error?: string }>("chat.send", {
        sessionKey,
        message: prompt,
        deliver: true,
      });
      notifyRunChatUpdated({ sessionKey });
      const direct = this._findLatestTranslateReply(
        [{ role: "assistant", content: String(response?.response || "") }],
        reqId,
      );
      if (direct) {
        this._displayTranslation = direct;
        this._displayTranslationLang = targetLang;
        this._statusNote = "";
        return;
      }
      if (response?.error) {
        this._error = response.error;
        return;
      }
      const deadline = Date.now() + NODE_REPORT_WAIT_MS;
      while (Date.now() <= deadline) {
        await new Promise<void>((resolve) => setTimeout(resolve, NODE_REPORT_POLL_MS));
        const messages = await this._readLatestChatMessages(sessionKey).catch(() => []);
        const translated = this._findLatestTranslateReply(messages, reqId);
        if (translated) {
          this._displayTranslation = translated;
          this._displayTranslationLang = targetLang;
          this._statusNote = "";
          return;
        }
      }
      this._error = "Translation did not return in time. Please try again.";
    } catch (error) {
      this._error = error instanceof Error ? error.message : String(error);
    } finally {
      this._translating = false;
    }
  }

  private _formatPendingRemaining(): string {
    if (!this._pendingDeadline) return "";
    const remainingMs = Math.max(0, this._pendingDeadline - (this._pendingTick || Date.now()));
    const totalSec = Math.ceil(remainingMs / 1000);
    const mins = Math.floor(totalSec / 60);
    const secs = totalSec % 60;
    if (mins > 0) return `${mins}m ${String(secs).padStart(2, "0")}s`;
    return `${secs}s`;
  }

  private _displayMarkdown(raw: string): string {
    const text = String(raw || "");
    if (this._committedReportSource === "agent-snapshot" || this.autoGenerate === false) {
      return normalizeNodeReportMarkdown(text);
    }
    return this._formatReportForDisplay(text);
  }

  private _formatReportForDisplay(raw: string): string {
    const input = normalizeNodeReportMarkdown(String(raw || "")).replace(/\r/g, "");
    if (!input.trim()) return "";
    if (/(^|\n)\s{0,3}#{2,6}\s+\S/m.test(input)) {
      return input.trim();
    }
    const lines = input.split("\n");
    const out: string[] = [];
    let withinSteps = false;
    let lastWasStep = false;
    let stepsEmitted = 0;
    let currentStepLabel = "";
    const currentCaseImages = new Map<string, { id: string; source: string; steps: Set<string> }>();
    const flushBlank = (): void => {
      if (out.length === 0 || out[out.length - 1] === "") return;
      out.push("");
    };

    const emit = (line: string): void => {
      out.push(line);
    };

    const sectionMatchers: Array<[RegExp, string]> = [
      [/^node\s+summary\s*:\s*$/i, "## Node Summary"],
      [/^features\s*:\s*$/i, "## Features"],
    ];

    const keyValue = (line: string): { key: string; value: string } | null => {
      const match = line.match(/^([A-Za-z][A-Za-z ]{1,40}):\s*(.*)$/);
      if (!match) return null;
      return { key: match[1].trim(), value: match[2].trim() };
    };

    const extractEvidenceSource = (value: string): string => {
      const text = String(value || "").trim();
      const media = text.match(/MEDIA:[^\s`"')]+/i);
      if (media) return media[0];
      const url = text.match(/(?:https?:\/\/|\/v1\/)[^\s`"')]+/i);
      if (url) return url[0];
      return "";
    };

    const registerEvidence = (source: string): string => {
      const key = source.trim();
      if (!key) return "";
      let entry = currentCaseImages.get(key);
      if (!entry) {
        const id = `IMG-${String(currentCaseImages.size + 1).padStart(3, "0")}`;
        entry = { id, source: key, steps: new Set<string>() };
        currentCaseImages.set(key, entry);
      }
      if (currentStepLabel) entry.steps.add(currentStepLabel);
      return entry.id;
    };

    const flushCaseEvidence = (): void => {
      if (!currentCaseImages.size) return;
      flushBlank();
      emit("**Evidence Images**");
      for (const entry of currentCaseImages.values()) {
        const usedBy = entry.steps.size ? `used by ${Array.from(entry.steps).join(", ")}` : "referenced";
        emit(`- **${entry.id}** (${usedBy})`);
        const isRenderable = /^(https?:\/\/|\/v1\/)/i.test(entry.source);
        if (isRenderable) {
          emit(`  - ![${entry.id}](${entry.source})`);
        } else {
          emit(`  - \`${entry.source}\``);
        }
      }
      currentCaseImages.clear();
    };

    for (const rawLine of lines) {
      let text = String(rawLine || "").trim();
      if (!text) {
        flushBlank();
        lastWasStep = false;
        continue;
      }

      // Normalize common model drift around step labels.
      text = text
        .replace(/^\d+\.\s*step[-\s]*(\d+)\s*:/i, "Step $1:")
        .replace(/^step[-\s]*(\d+)\s*:/i, "Step $1:");

      for (const [re, mapped] of sectionMatchers) {
        if (re.test(text)) {
          flushBlank();
          emit(mapped);
          flushBlank();
          withinSteps = false;
          lastWasStep = false;
          text = "";
          break;
        }
      }
      if (!text) continue;

      if (/^feature\s+f-\d+/i.test(text)) {
        flushCaseEvidence();
        flushBlank();
        emit(`### ${text}`);
        withinSteps = false;
        lastWasStep = false;
        stepsEmitted = 0;
        currentStepLabel = "";
        continue;
      }
      if (/^test\s+case\s+tc-\d+/i.test(text)) {
        flushCaseEvidence();
        flushBlank();
        emit(`### ${text}`);
        withinSteps = false;
        lastWasStep = false;
        stepsEmitted = 0;
        currentStepLabel = "";
        continue;
      }
      if (/^steps?\s*:\s*$/i.test(text)) {
        flushBlank();
        emit("#### Steps");
        withinSteps = true;
        lastWasStep = false;
        stepsEmitted = 0;
        currentStepLabel = "";
        continue;
      }
      if (/^step\s+\d+\s*:/i.test(text)) {
        const m = text.match(/^step\s+(\d+)\s*:\s*(.*)$/i);
        const stepNo = m?.[1] ?? "";
        const action = (m?.[2] ?? "").trim();
        if (stepsEmitted > 0) flushBlank();
        emit(`${stepNo}. ${action}`);
        withinSteps = true;
        lastWasStep = true;
        stepsEmitted += 1;
        currentStepLabel = `Step ${stepNo}`;
        continue;
      }

      const bulletSub = text.match(
        /^[-*+]\s*(Expected Result|Evidence Image|Evidence)\s*:\s*(.*)$/i,
      );
      if (bulletSub && withinSteps) {
        const label = bulletSub[1].toLowerCase().startsWith("evidence") ? "Evidence" : "Expected Result";
        const value = (bulletSub[2] ?? "").trim() || "NONE";
        if (label === "Evidence") {
          if (value.toUpperCase() === "NONE") {
            emit(`   - **Evidence:** NONE`);
          } else {
            const source = extractEvidenceSource(value);
            if (!source) {
              emit(`   - **Evidence:** ${value}`);
            } else {
              const evidenceId = registerEvidence(source);
              emit(`   - **Evidence Ref:** ${evidenceId}`);
            }
          }
        } else {
          emit(`   - **Expected Result:** ${value}`);
        }
        lastWasStep = true;
        continue;
      }

      const kv = keyValue(text);
      if (kv) {
        const lower = kv.key.toLowerCase();
        const inlineFields = new Set([
          "description",
          "purpose",
          "objective",
          "preconditions",
          "test data",
          "in-scope behaviors",
          "expected result",
          "evidence image",
        ]);
        if (inlineFields.has(lower)) {
          const value = kv.value || "NONE";
          if (lower === "expected result") {
            const prefix = withinSteps && lastWasStep ? "   - " : "- ";
            emit(`${prefix}**${kv.key}:** ${value}`);
          } else if (lower === "evidence image") {
            const prefix = withinSteps && lastWasStep ? "   - " : "- ";
            if (value.toUpperCase() === "NONE") {
              emit(`${prefix}**Evidence:** NONE`);
            } else {
              const source = extractEvidenceSource(value);
              if (!source) {
                emit(`${prefix}**Evidence:** ${value}`);
              } else {
                const evidenceId = registerEvidence(source);
                emit(`${prefix}**Evidence Ref:** ${evidenceId}`);
              }
            }
          } else {
            emit(`- **${kv.key}:** ${value}`);
          }
          lastWasStep = lower === "expected result" || lower === "evidence image";
          continue;
        }
      }

      // Preserve plain list lines, but normalize stray bullets.
      text = text.replace(/^[-*]\s*$/, "").trim();
      if (!text) continue;
      emit(text);
      lastWasStep = false;
    }
    flushCaseEvidence();

    return out.join("\n").replace(/\n{3,}/g, "\n\n").trim();
  }

  render(): ReturnType<typeof html> {
    const hasPending = Boolean(this._pendingReqId);
    const canonicalKey = String(this.nodeKey || "").trim();
    const reportMatchesNode = Boolean(
      this._report?.content?.trim()
      && (this._report?.nodeKey === this._scopeKey()
        || this._report?.nodeKey === canonicalKey),
    );
    const breadcrumb = formatPathBreadcrumb(this.parents || [], this.title || this.nodeKey);

    const pendingMessage = "Generating full PFM node report, please wait…";
    const isGeneratingFullReport = hasPending || this._agentGeneratingOnDemand;
    const currentReportLanguage = this._currentReportLanguage();
    const canTranslate = Boolean(
      reportMatchesNode
      && this._report?.content?.trim()
      && currentReportLanguage !== "en"
      && !isGeneratingFullReport
      && !this._translating,
    );
    const refreshButtonLabel = isGeneratingFullReport
      ? this._t("nodeReport.pleaseWait")
      : this._reportTier === "summary" || this._reportTier === "incomplete"
        ? this._t("nodeReport.generateFull")
        : !this.autoGenerate
          ? this._t("nodeReport.refresh")
          : reportMatchesNode && this._report
            ? this._t("nodeReport.refreshPfm")
            : this._t("nodeReport.generatePfm");
    const showingTranslatedView = Boolean(this._displayTranslation && this._displayTranslationLang !== "en");
    const activeDisplayMarkdown = this._displayTranslation ?? this._report?.content ?? "";
    const activeDisplayIsFull = activeDisplayMarkdown
      ? isValidNodeReportMarkdown(activeDisplayMarkdown)
        || isIncompleteExplorationNodeReport(activeDisplayMarkdown)
      : false;
    const translationBanner = this._translating
      ? html`<div style="color:#93c5fd;margin-bottom:12px;">${this._t("nodeReport.translating")}</div>`
      : showingTranslatedView
        ? html`<div style="color:#93c5fd;margin-bottom:12px;">${this._t("nodeReport.translatedViewNote", { language: nodeReportLanguageProfile(this._displayTranslationLang || "en").name })}</div>`
        : "";
    const pendingStatusStyle =
      "display:flex;align-items:center;gap:10px;margin-bottom:14px;padding:14px 16px;border-radius:10px;"
      + "border:1px solid color-mix(in srgb,#a78bfa 55%,transparent);"
      + "background:color-mix(in srgb,#7c3aed 16%,transparent);"
      + "color:#ddd6fe;font-size:14px;line-height:1.5;font-weight:600;"
      + "box-shadow:0 0 0 1px color-mix(in srgb,#a78bfa 22%,transparent);";

    const pendingBanner = isGeneratingFullReport
      ? html`
          <div style="${pendingStatusStyle}" role="status" aria-live="polite">
            <span
              style="display:inline-block;width:10px;height:10px;border-radius:999px;background:#c4b5fd;flex-shrink:0;"
              aria-hidden="true"
            ></span>
            <span>${pendingMessage}</span>
          </div>
        `
      : "";

    const phase = this._resolvedSnapshotPhase();
    const phaseLabel =
      phase === "evolving" || phase === "finalizing" || phase === "final"
        ? this._pfmVersionLabel() + (phase === "finalizing" ? " — finalizing" : "")
        : "";
    const sourceLabel =
      this._committedReportSource === "agent-snapshot"
        ? phaseLabel
          ? `Agent snapshot (${phaseLabel})`
          : `Agent snapshot (tree v${this._committedTreeVersion || "?"})`
        : this._committedReportSource === "chat-generated"
          ? "Chat-generated draft"
          : "";


    const readBaseline = this._sourceExecutionId || (this.pfmArtifactReadExecutionId || "").trim();
    const showingPriorRunReport =
      this._availability === "baseline"
      || (Boolean(readBaseline) && readBaseline !== (this.executionId || "").trim());
    const baselineNote =
      showingPriorRunReport
        ? html`
            <div
              style="margin-bottom:10px;font-size:11px;color:var(--muted);padding:8px 10px;border-radius:8px;border:1px solid color-mix(in srgb,var(--border) 70%,transparent);"
            >
              This run has not saved a report for this node yet. Showing the report from the previous run.
            </div>
          `
        : "";

    const summaryNote =
      this._reportTier === "summary"
      && this._availability !== "pending_generation"
      && !hasPending
      && !isGeneratingFullReport
        ? html`
            <div
              style="margin-bottom:12px;font-size:12px;padding:8px 10px;border-radius:8px;border:1px solid color-mix(in srgb,var(--border) 70%,transparent);color:var(--muted);"
            >
              Brief summary from delivery. Click <strong>Refresh</strong> to generate a full report for this run.
            </div>
          `
        : "";

    const incompleteNote =
      this._reportTier === "incomplete"
      && this._availability !== "pending_generation"
      && !hasPending
      && !isGeneratingFullReport
        ? html`
            <div
              style="margin-bottom:12px;font-size:12px;padding:8px 10px;border-radius:8px;border:1px solid color-mix(in srgb,var(--border) 70%,transparent);color:var(--muted);"
            >
              ${this._t("nodeReport.incompleteExplorationNote")}
            </div>
          `
        : "";

    const pendingNote =
      this._availability === "pending_generation" && !hasPending && !this.autoGenerateOnDemand
        ? html`
            <div
              style="margin-bottom:12px;font-size:13px;line-height:1.5;padding:12px 14px;border-radius:8px;border:1px dashed color-mix(in srgb,var(--border) 65%,transparent);color:var(--muted);"
              role="status"
            >
              <strong>Report not ready yet.</strong>
              This node is on the map; its report will appear after the next agent delivery.
            </div>
          `
        : "";

    return html`
      <div class="node-report-panel">
        ${baselineNote}
        ${summaryNote}
        ${incompleteNote}
        ${pendingNote}
        <div class="node-report-header">
          <div>
            <div class="node-report-title">${this.title || this.nodeKey || "PFM Node"}</div>
            <div class="node-report-meta">
              ${canonicalKey
                ? html`Node key: <code style="font-size:11px;">${canonicalKey}</code> · Path: ${breadcrumb}`
                : html`Path: ${breadcrumb}`}
              ${isGeneratingFullReport
                ? html`<br /><span style="color:#c4b5fd;font-weight:600;">${pendingMessage}</span>`
                : reportMatchesNode && this._report
                  ? html`<br />Saved ${new Date(this._report.generatedAt).toLocaleString()}${this._reportTier === "full" && this._reportRevision > 0 ? html` · Map v${this._reportRevision}` : ""}${sourceLabel ? html` · ${sourceLabel}` : ""}`
                  : ""}
            </div>
          </div>
          <div style="position:relative;">
            <button
              class="btn btn-sm btn-ghost refresh-menu-trigger"
              type="button"
              title=${this._t("nodeReport.refreshMenu")}
              aria-label=${this._t("nodeReport.refreshMenu")}
              ?disabled=${isGeneratingFullReport || this._translating}
              @click=${() => {
                this._refreshMenuOpen = !this._refreshMenuOpen;
              }}
            >
              <span style="display:inline-flex;width:14px;height:14px;">${icons.refresh}</span>
            </button>
            ${this._refreshMenuOpen
              ? html`
                  <div
                    style="position:absolute;right:0;top:calc(100% + 6px);min-width:170px;padding:6px;border-radius:10px;border:1px solid color-mix(in srgb,var(--border) 80%,transparent);background:color-mix(in srgb,var(--card, #161920) 92%, transparent);z-index:10;box-shadow:0 10px 25px rgba(0,0,0,0.25);display:grid;gap:4px;"
                  >
                    <button
                      class="btn btn-sm"
                      type="button"
                      ?disabled=${!canTranslate}
                      title=${currentReportLanguage === "en"
                        ? this._t("nodeReport.translateDisabledEnglish")
                        : ""}
                      @click=${() => void this._translateForDisplay()}
                    >
                      ${this._t("nodeReport.translate")}
                    </button>
                    <button
                      class="btn btn-sm"
                      type="button"
                      ?disabled=${isGeneratingFullReport || this._translating}
                      @click=${() => void this._generate(true)}
                    >
                      ${this._t("nodeReport.regenerate")}
                    </button>
                  </div>
                `
              : ""}
          </div>
        </div>
        <div class="node-report-body">
          ${pendingBanner}
          ${this._error
            ? html`<div style="color:#fca5a5;margin-bottom:12px;">${this._error}</div>`
            : ""}
          ${this._statusNote
            ? html`<div style="color:#93c5fd;margin-bottom:12px;">${this._statusNote}</div>`
            : ""}
          ${translationBanner}
          ${this._loading && !this._report?.content?.trim() && !isGeneratingFullReport
            ? html`<div style="color:var(--muted);">Loading saved report…</div>`
            : isGeneratingFullReport
              ? ""
              : reportMatchesNode && this._report?.content && this._availability !== "pending_generation"
              ? isMaterializedStubMarkdown(this._report.content)
                ? html`
                    <div style="margin-bottom:12px;font-size:12px;padding:8px 10px;border-radius:8px;border:1px solid color-mix(in srgb,var(--border) 70%,transparent);color:var(--muted);">
                      Saved report is a <strong>placeholder</strong> (not a complete node report yet). Try again after the next agent delivery.
                    </div>
                    ${unsafeHTML(toSanitizedMarkdownHtml(this._displayMarkdown(activeDisplayMarkdown)))}
                  `
                : !activeDisplayIsFull
                  ? html`
                      <div style="margin-bottom:12px;font-size:12px;padding:8px 10px;border-radius:8px;border:1px solid color-mix(in srgb,#fca5a5 45%,transparent);color:#fecaca;">
                        ${this._t("nodeReport.longFormatRequired")}
                      </div>
                      ${unsafeHTML(toSanitizedMarkdownHtml(this._displayMarkdown(activeDisplayMarkdown)))}
                    `
                  : html`${unsafeHTML(toSanitizedMarkdownHtml(this._displayMarkdown(activeDisplayMarkdown)))}`
                : this._availability === "pending_generation"
                  ? ""
                  : html`<div style="color:var(--muted);">No saved report for this node yet.</div>`}
        </div>
      </div>
    `;
  }

  private _t(key: string, params?: Record<string, string | number>): string {
    return t(key, params, this.locale as UiLocale);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "ead-pfm-node-report-panel": EadPfmNodeReportPanel;
  }
}
